// AuthStatusForm Component
// This file defines a form for updating the authorization status of a procedure, including notes and closing reasons.

import { useReducer, FormEvent } from "react";
import { styled } from "@stitches/react";
import { AuthStatusFormFields } from "@/types/my-tasks";
import { AuthStatus, Procedure } from "@/types/procedure";
import {
  Button,
  Form,
  FormField,
  Textarea,
  RadioGroup,
  FormFieldLayout,
  Label,
} from "@/vendor/Forge/dist";

import { useUpdateAuthStatus } from "@/hooks/use-case-details";

// Styled wrapper for the form
const Wrapper = styled("div", {
  padding: "0 4px",
});

// Styled container for form call-to-action buttons
const CTAs = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  width: "100%",
  marginTop: 28,
});

// Styled text for form labels and values
const FormText = styled("p", {
  fontSize: "16px",
  lineHeight: "20px",
  fontWeight: 400,
  color: "#000 !important",
});

// List of possible reasons for closing an authorization
const closingReasons = [
  "Authorization is denied",
  "Surgery is cancelled",
  "Other",
];

/**
 * Determines if an additional custom action note is required based on form values and status.
 * @param formValues - Current form field values
 * @param newStatus - The new authorization status
 * @returns True if a note is required, false otherwise
 */
const isAdditionalCustomActionNoteRequired = (
  formValues: AuthStatusFormFields,
  newStatus: AuthStatus,
) => {
  if (newStatus === "Closed") {
    return (
      !!formValues.closingReason &&
      formValues.closingReason !== "Surgery is cancelled"
    );
  }

  return false;
};

/**
 * Validates the form based on the new status and field values.
 * @param formValues - Current form field values
 * @param newStatus - The new authorization status
 * @returns True if the form is valid, false otherwise
 */
export const isValid = (
  formValues: AuthStatusFormFields,
  newStatus: AuthStatus,
) => {
  if (newStatus === "Closed") {
    if (!formValues.closingReason) return false;
    if (isAdditionalCustomActionNoteRequired(formValues, newStatus))
      return !!formValues.notes;
  }

  return true;
};

/**
 * Props for the AuthStatusForm component
 */
export interface Props {
  procedure: Procedure;
  newStatus: AuthStatus;
  onSubmit: () => void;
  onCancelClick: () => void;
}

/**
 * AuthStatusForm Component
 * Renders a form for updating the authorization status of a procedure, including notes and closing reasons.
 * Handles form validation, submission, and displays relevant procedure/auth info.
 *
 * @param procedure - The procedure being updated
 * @param newStatus - The new authorization status
 * @param onSubmit - Callback for successful form submission
 * @param onCancelClick - Callback for cancel action
 */
const AuthStatusForm = ({
  procedure,
  newStatus,
  onSubmit,
  onCancelClick,
}: Props) => {
  // Hook for updating the authorization status
  const { isPending: isCreatingNotes, updateStatus } = useUpdateAuthStatus(
    procedure.caseNumber,
  );

  // Reducer for managing form field values
  const [formValues, setFormValues] = useReducer(
    (state: AuthStatusFormFields, changes: Partial<AuthStatusFormFields>) => ({
      ...state,
      ...changes,
    }),
    {
      notes: "",
      closingReason: "",
    },
  );

  /**
   * Handles form submission, updating the status and calling the onSubmit callback.
   */
  const handleSubmit = (e: FormEvent<HTMLElement>) => {
    e.preventDefault();

    if (newStatus === "Obtained") {
      updateStatus({
        authStatus: newStatus,
        authorizationNumber: procedure.authInfo.authorizationNumber,
        beginDate: procedure.authInfo.beginDate,
        endDate: procedure.authInfo.endDate,
        actionNote: formValues.notes,
        insuranceNumber: procedure.authInfo.insuranceInfo?.insuranceNumber,
      });
    } else {
      updateStatus({
        authStatus: newStatus,
        actionNote: formValues.notes,
        reasonForClosing: formValues.closingReason ?? undefined,
      });
    }

    onSubmit();
  };

  return (
    <Wrapper>
      <Form
        noValidate
        includeSubmitButton={false}
        labelAlwaysAbove
        labelWidth={12}
        buttonText="Change Auth Status"
        onSubmit={handleSubmit}
      >
        {/* Display procedure and patient info */}
        <p>
          Please complete for{" "}
          {`"${
            procedure.procedureInformation?.updatedProcedureName ??
            String(procedure.procedureInformation.scheduledprocedureName)
          }"`}{" "}
          for {procedure.athenaPatientDetails?.patientName}
        </p>
        {/* Show insurance and auth info if status is 'Obtained' */}
        {newStatus === "Obtained" && (
          <>
            <Label text="INSURANCE" />
            <FormText>
              {procedure.authInfo.insuranceInfo?.insurancePlanDisplayName}{" "}
              {procedure.authInfo.insuranceInfo?.insuranceNumber}
            </FormText>
            <Label text="AUTHORIZATION NUMBER" />
            <FormText>{procedure.authInfo.authorizationNumber}</FormText>
            <Label text="BEGINNING DATE" />
            <FormText>{procedure.authInfo.beginDate}</FormText>
            <Label text="END DATE" />
            <FormText>{procedure.authInfo.endDate}</FormText>
          </>
        )}
        {/* Show closing reason radio group if status is 'Closed' */}
        {newStatus === "Closed" && (
          <>
            <FormField
              required
              id="reason-for-closing"
              labelText="Reason for Closing"
              name="closingReason"
              options={closingReasons}
              value={formValues.closingReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({ closingReason: e.target.value })
              }
              inputAs={RadioGroup}
            />
          </>
        )}
        {/* Notes textarea, required if needed by status/reason */}
        <FormField
          required={isAdditionalCustomActionNoteRequired(formValues, newStatus)}
          id="custom-action-note"
          name="notes"
          labelText="Additional Custom Action Note"
          value={formValues.notes}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues({ notes: e.target.value })
          }
          inputAs={Textarea}
        />
        {/* Form action buttons: submit and cancel */}
        <FormFieldLayout
          inputSlot={
            <CTAs>
              <Button
                fullWidth
                type="submit"
                disabled={!isValid(formValues, newStatus) || isCreatingNotes}
              >
                {isCreatingNotes ? "Updating Status..." : "Change Auth Status"}
              </Button>
              <Button
                fullWidth
                type="button"
                variant="secondary"
                onClick={onCancelClick}
              >
                Cancel
              </Button>
            </CTAs>
          }
        />
      </Form>
    </Wrapper>
  );
};

export default AuthStatusForm;
